import React, { useEffect, useRef, useState } from "react";
import api, { downloadFileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollText, Download, Upload, Trash2, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

function sizeLabel(bytes) {
  if (!bytes) return "";
  const kb = bytes / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
}

export default function Listino() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get("/listino");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    form.append("title", title);
    try {
      await api.post("/listino", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Listino caricato");
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const download = async (it) => {
    setDownloadingId(it.id);
    try {
      await downloadFileUrl(it.url, it.filename);
    } catch {
      toast.error("Errore nel download");
    } finally {
      setDownloadingId(null);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare "${it.title}"?`)) return;
    await api.delete(`/listino/${it.id}`);
    toast.success("Listino eliminato");
    load();
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto">
      <h1 className="text-3xl font-extrabold text-navy tracking-tight mb-1 flex items-center gap-2">
        <ScrollText className="text-primary" size={26} /> Listino
      </h1>
      <p className="text-muted-foreground mb-8">
        {isAdmin ? "Carica i listini che i commerciali potranno scaricare." : "Scarica il listino prezzi aggiornato della piattaforma."}
      </p>

      {isAdmin && (
        <div className="bg-white border border-border rounded-xl p-6 mb-8">
          <h2 className="font-extrabold text-navy mb-4">Carica un nuovo listino</h2>
          <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end">
            <div>
              <Label htmlFor="lt">Titolo (opzionale)</Label>
              <Input id="lt" value={title} data-testid="listino-title-input" onChange={(e) => setTitle(e.target.value)} placeholder="Es. Listino BSpanel 2026" className="mt-1.5" />
            </div>
            <div>
              <input ref={fileRef} type="file" accept=".pdf,image/*" onChange={onUpload} className="hidden" data-testid="listino-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="listino-upload-button" className="gap-2 font-bold w-full">
                <Upload size={16} /> {uploading ? "Caricamento..." : "Carica file"}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Formati supportati: PDF, PNG, JPG.</p>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground">Caricamento...</p>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-16 text-center bg-white">
          <ScrollText className="mx-auto text-muted-foreground mb-3" size={40} />
          <p className="text-navy font-bold">Nessun listino disponibile</p>
          {!isAdmin && <p className="text-muted-foreground text-sm mt-1">Torna a controllare più tardi.</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const isPdf = (it.content_type || "").includes("pdf");
            return (
              <div key={it.id} data-testid={`listino-item-${it.id}`} className="bg-white border border-border rounded-xl p-4 flex items-center gap-4">
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${isPdf ? "bg-destructive/10 text-destructive" : "bg-accent/10 text-accent"}`}>
                  {isPdf ? <FileText size={20} /> : <ImageIcon size={20} />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-navy truncate">{it.title}</p>
                  <p className="text-xs text-muted-foreground">{(it.created_at || "").slice(0, 10)} · {sizeLabel(it.size)}</p>
                </div>
                <Button onClick={() => download(it)} disabled={downloadingId === it.id} data-testid={`listino-download-${it.id}`} className="gap-2 font-bold shrink-0">
                  <Download size={16} /> {downloadingId === it.id ? "..." : "Scarica"}
                </Button>
                {isAdmin && (
                  <button onClick={() => remove(it)} data-testid={`listino-delete-${it.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200 shrink-0">
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
